import { createUseStyles } from 'react-jss';
import { ICareMap } from '../caremap/ICareMap';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../caremap/CareMapState';
import { useState } from 'react';
import { useSubscription } from '@meraki-internal/state';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { NavigationCurrentEntity } from './NavigationCurrentEntity';
import { DrawerSectionHeading } from '../components/DrawerSectionHeading';
import { IonButton, IonPopover, IonText } from '@ionic/react';
import { ArrowRightArrowLeftIcon } from '../theme/Icons';
import { MenuItem } from '../components/MenuItem';
import { Divider } from '../components/Divider';
import { CareMapMenuItem } from '../caremap/CareMapMenuItem';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { AppPrincipal, isCaremapViewer } from '../auth/AppPrincipal';
import { CompositeAnonymousHttpClient } from '../auth/anonymous/CompositeAnonymousHttpClient';
import dayjs from 'dayjs';

const AnonymousSessionExpirationHint: React.FC<{ careMapId: string; userId: string; }> = ({ careMapId, userId }) => {
    const client = useInstance(CompositeAnonymousHttpClient);
    const expirationEpochSeconds = client.getAccessTokenExpirationForCaremap({ careMapId, userId });
    const expirationISO = new Date(expirationEpochSeconds * 1000).toISOString();

    return (
        <IonText color="medium" style={{ fontSize: 10, marginLeft: 10}}>Access expires on {dayjs(expirationISO).format('MMM D')} at {dayjs(expirationISO).format('h:mm a')}</IonText>
    );
};

interface IStyleProps {
    width: number;
};

const useStyles = createUseStyles<'recipientMenuPopover', IStyleProps>({
    recipientMenuPopover: {
        '& hr': {
            marginBlockStart: 0,
            marginBlockEnd: 0
        },

        '&::part(content)': {
            minWidth: ({width}) => `${width-50}px`,
            maxWidth: ({width}) => `${width-50}px`
        }
    }
});

interface ICaremapSwitcherProps {
    popoverWidth: number;
    closeNav: () => Promise<void>;
}

export const CaremapSwitcher: React.FC<ICaremapSwitcherProps> = ({ popoverWidth, closeNav }) => {
    const careMapStateInstance = useInstance(CareMapState);
    const [isRecipientMenuOpen, setRecipientMenuOpen] = useState<boolean>(false);
    const tracking = useInstance(MixPanelEventEmitter);
    const history = useInstance(HistoryViewModel);

    const isAnonymous = isCaremapViewer(useInstance(AppPrincipal));

    const classes = useStyles({ width: popoverWidth });

    useSubscription(() => careMapStateInstance);

    const careMaps = careMapStateInstance.getCareMaps();
    const hasMultipleCareMaps = careMaps.length > 1;
    const activeCareMap = careMapStateInstance.getActiveCaremap();

    const toggleRecipientMenu = () => {
        setRecipientMenuOpen(oldState => {
            return !oldState;
        });
    };

    const activeCaremapToShowInSwitcher = careMapStateInstance.getMostRecentCaremap()!;


    // note we don't expect new recipient to happen in  the new UI, but could still exist in old data, and in the old UI
    const patientName = careMapStateInstance.getPatientFullName(activeCaremapToShowInSwitcher) || 'New Recipient';

    const switchRecipient = async (careMap: ICareMap) => {
        if (activeCareMap?.careMapId !== careMap.careMapId) {
            setRecipientMenuOpen(false);

            tracking.track('Care Recipient Switched');

            await closeNav();

            // if caremapid in the route, swap it
            const activeCaremapId = activeCaremapToShowInSwitcher.careMapId;
            const currentPath = history.getCurrentLocation().pathname;
            if (currentPath.includes(activeCaremapId) && !currentPath.includes('/q/')) {
                const newPath = currentPath.replace(activeCaremapId, careMap.careMapId);
                history.push(newPath);
            }
            // otherwise if new ui, try to stay on current page
            else {
                const pathParts = currentPath.split('/');
                if (pathParts[1] === 'caregiver' || pathParts[1] === 'q') {
                    history.push(`/caregiver?id=${careMap.careMapId}`);
                }
                else {
                    // all other urls should be following
                    // /[care]/:caremapId/*
                    // we can't assume the * is desired (eg could be editing a caremap specific note)
                    // but we can switch to the same root
                    // (exception is orgs... but they don't use this function, b/c they go to clients)
                    history.push(`/${pathParts[1]}/${careMap.careMapId}`);
                }
                // if we need a fallback go to care
            }
        }
    };

    return (
        <>
            <div style={{marginTop: 20}}>
                <NavigationCurrentEntity>
                    <div>
                        <DrawerSectionHeading text="Care Recipient" tight></DrawerSectionHeading>
                        <IonText>{patientName}</IonText>
                        {isAnonymous && activeCareMap && (
                            <AnonymousSessionExpirationHint careMapId={activeCareMap.careMapId!} userId={(activeCareMap as ICareMap).userId} />
                        )}

                    </div>
                    {hasMultipleCareMaps && (
                        <IonButton
                            fill="clear"
                            style={{marginBottom: -8}}
                            onClick={toggleRecipientMenu}
                            id="switch-recipient-button"
                        >
                            <ArrowRightArrowLeftIcon />&nbsp;switch
                        </IonButton>
                    )}
                </NavigationCurrentEntity>
            </div>

            {hasMultipleCareMaps && (
                <IonPopover
                    alignment="end"
                    trigger="switch-recipient-button"
                    isOpen={isRecipientMenuOpen}
                    onDidDismiss={() => setRecipientMenuOpen(false)}
                    className={classes.recipientMenuPopover}
                >
                    <MenuItem data-id="switch-recipient" label="Switch Care Recipient" heading />
                    <Divider />
                    {careMaps.map((map, idx) => {
                        const isLast = idx+1 === careMaps.length;
                        return map.careMapId !== activeCareMap?.careMapId ? (
                            <div key={map.careMapId}>
                                <CareMapMenuItem careMap={map} onClick={switchRecipient} />
                                {!isLast && <Divider />}
                            </div>
                        ) : null;
                    })}
                </IonPopover>
            )}
        </>
    );
};
